import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExternalLink, History, Crown, Zap, Filter, Loader2 } from "lucide-react";
import type { CreditTransaction, PageInfo } from "../types";

interface CreditHistoryTabProps {
  transactions: CreditTransaction[];
  transactionsLoading: boolean;
  transactionsPageInfo: PageInfo;
  loadingMoreTransactions: boolean;
  handleLoadMoreTransactions: () => Promise<void>;
}

type FilterType = 'all' | 'credit' | 'debit';
type SourceFilter = 'all' | 'SUBSCRIPTION' | 'PREPAID' | 'BILLABLE';

function formatUsdLabel(amountInCredits: number) {
  const amountInUsd = Math.abs(amountInCredits) / 100;
  const normalizedAmount = Number.isInteger(amountInUsd) ? amountInUsd.toFixed(0) : amountInUsd.toFixed(2);
  return `${normalizedAmount} USD`;
}

function transactionDescription(txn: CreditTransaction) {
  const description = txn.description?.trim();
  if (!description) {
    return txn.sessionId ? "Session usage" : "N/A";
  }

  if (
    txn.creditSource === "PREPAID" &&
    txn.transactionType === "credit" &&
    /^(?:[A-Za-z]+ )?prepaid top-up \(/i.test(description)
  ) {
    return `Prepaid topup for ${formatUsdLabel(txn.amount)}`;
  }

  return description;
}

export function CreditHistoryTab({
  transactions,
  transactionsLoading,
  transactionsPageInfo,
  loadingMoreTransactions,
  handleLoadMoreTransactions,
}: CreditHistoryTabProps) {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<FilterType>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const initialLoading = transactionsLoading && transactions.length === 0;

  const filteredTransactions = transactions.filter((txn) => {
    if (typeFilter !== 'all' && txn.transactionType !== typeFilter) {
      return false;
    }
    if (sourceFilter !== 'all' && txn.creditSource !== sourceFilter) {
      return false;
    }
    return true;
  });

  const getSourceBadge = (source?: 'SUBSCRIPTION' | 'PREPAID' | 'BILLABLE') => {
    if (source === 'SUBSCRIPTION') {
      return (
        <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30 gap-1">
          <Crown className="w-3 h-3" />
          Subscription
        </Badge>
      );
    }
    if (source === 'PREPAID') {
      return (
        <Badge variant="outline" className="text-xs text-green-500 border-green-500/30 gap-1">
          <Zap className="w-3 h-3" />
          Prepaid
        </Badge>
      );
    }
    if (source === 'BILLABLE') {
      return (
        <Badge variant="outline" className="text-xs text-blue-500 border-blue-500/30 gap-1">
          <Zap className="w-3 h-3" />
          Billable
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter by:</span>
        </div>
        
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as FilterType)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="credit">Credits</SelectItem>
            <SelectItem value="debit">Debits</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as SourceFilter)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="SUBSCRIPTION">Subscription</SelectItem>
            <SelectItem value="PREPAID">Prepaid</SelectItem>
            <SelectItem value="BILLABLE">Billable</SelectItem>
          </SelectContent>
        </Select>

        {(typeFilter !== 'all' || sourceFilter !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setTypeFilter('all');
              setSourceFilter('all');
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Transaction History Table */}
      <Card className="overflow-hidden border-border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 text-sm font-semibold">Date</th>
                <th className="text-left p-4 text-sm font-semibold">Type</th>
                <th className="text-left p-4 text-sm font-semibold">Source</th>
                <th className="text-left p-4 text-sm font-semibold">Description</th>
                <th className="text-right p-4 text-sm font-semibold">Amount</th>
                <th className="text-center p-4 text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {initialLoading ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-muted-foreground">
                    Loading transactions...
                  </td>
                </tr>
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    <div className="space-y-2">
                      <History className="w-12 h-12 mx-auto text-muted-foreground/50" />
                      <p>No transactions found</p>
                      <p className="text-xs">
                        {typeFilter !== 'all' || sourceFilter !== 'all' 
                          ? 'Try adjusting your filters' 
                          : 'Your credit history will appear here'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((txn) => {
                  const isCredit = txn.transactionType === 'credit';
                  const formattedDate = new Date(txn.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  });
                  const formattedTime = new Date(txn.createdAt).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  
                  return (
                    <tr key={txn.id} className="hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div className="text-base">{formattedDate}</div>
                        <div className="text-xs text-muted-foreground">{formattedTime}</div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isCredit ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {isCredit ? 'Credit' : 'Debit'}
                        </span>
                      </td>
                      <td className="p-4">
                        {getSourceBadge(txn.creditSource)}
                      </td>
                      <td className="p-4 text-base text-muted-foreground">
                        {transactionDescription(txn)}
                      </td>
                      <td className={`p-4 text-right font-medium text-base ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                        {isCredit ? '+' : '-'}{Math.abs(txn.amount).toLocaleString()}
                      </td>
                      <td className="p-4 text-center">
                        {txn.sessionId ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/history/${txn.sessionId}`)}
                            className="h-8 px-2"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {transactions.length > 0 && transactionsPageInfo.hasNextPage && (
          <div className="border-t border-border px-4 py-4">
            <Button
              variant="outline"
              onClick={() => void handleLoadMoreTransactions()}
              disabled={loadingMoreTransactions}
              className="w-full sm:w-auto"
            >
              {loadingMoreTransactions ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading more
                </>
              ) : (
                "Load more transactions"
              )}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
